import { format } from 'date-fns';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { defaultDateFnsLocale } from 'helpers/locale';
import moment from 'moment-timezone';
import { formatterTooltipValues } from '../../helpers';
import {
  Label,
  Title,
  TooltipContainer,
  TooltipContent,
  TooltipRowContainer,
} from './styles';
import { colors } from '~/styles/colors';
import { EnergyTrendsData } from '../../types';

interface EnergyTrendsTooltipGraphProps {
  index: number;
  trendsData: EnergyTrendsData['trendsData'];
}

export function EnergyTrendsTooltipGraph({
  index,
  trendsData,
}: Readonly<EnergyTrendsTooltipGraphProps>): ReactElement {
  const { t } = useTranslation();
  const trendDay = trendsData[index];

  const formattedDate = format(new Date(trendDay.time), 'P', { locale: defaultDateFnsLocale });

  const previousTrendDay = index > 0
    ? trendsData[index - 1]
    : undefined;

  const consumptionDay = generateConsumptionDayValue();

  const numberConsumptionPercentageFixed = Number(trendDay.consumptionPercentage).toFixed();

  const isDangerPercentage = Number(numberConsumptionPercentageFixed) > 100;

  const { label, value } = formatLabelConsumption();

  function generateConsumptionDayValue(): number {
    if (trendDay.consumption !== 0 && previousTrendDay) {
      return trendDay.consumption - previousTrendDay.consumption;
    }

    return Number(trendDay.consumption);
  }

  function formatLabelConsumption(): { label: string, value: number } {
    const isAfterOrSameActualDate = moment(trendDay.time).startOf('day').isSameOrAfter(moment().startOf('day'));

    let label = '';
    let value = 0;

    if (isAfterOrSameActualDate) {
      label = t('consumoProjetadoMin');
      value = trendDay.consumptionForecast;
    } else {
      label = t('consumido');
      value = trendDay.lastConsumption ?? 0;
    }

    return {
      label,
      value,
    };
  }

  return (
    <TooltipContainer data-testId="energy-trends-tooltip">
      <Label>
        {formattedDate}
      </Label>
      <TooltipContent>
        <TooltipRowContainer>
          <Title>
            {t('consumoDoDia')}
            :
          </Title>
          <Label>
            {formatterTooltipValues(consumptionDay)}
          </Label>
        </TooltipRowContainer>
        <TooltipRowContainer>
          <Title>
            {t('meta')}
            :
          </Title>

          <Label>
            {formatterTooltipValues(trendDay.consumptionTarget)}
          </Label>
        </TooltipRowContainer>
        <TooltipRowContainer>
          <Title>
            {label}
            :
          </Title>
          <Label>
            {formatterTooltipValues(value)}
          </Label>
          <div>
            <Label style={{
              color: isDangerPercentage ? colors.state.danger.base : colors.state.success.base,
            }}
            >
              {numberConsumptionPercentageFixed}
              %
            </Label>
          </div>
        </TooltipRowContainer>
      </TooltipContent>
    </TooltipContainer>
  );
}
